import {useRef, useCallback, useEffect, type ChangeEvent, type PointerEvent as ReactPointerEvent} from 'react'
import {Box, TextField} from '@mui/material'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── Constants ─────────────────────────────────────────────────────

const PAD_SIZE = 120
const HANDLE_RADIUS = 7

// ── OffsetPad — 2D square pad with draggable ball + X/Y inputs ────

interface OffsetPadProps {
  valueX: number
  valueY: number
  min: number
  max: number
  minX?: number
  maxX?: number
  minY?: number
  maxY?: number
  /** Snap increment for pad drag and input arrows (default: 1) */
  step?: number
  onChangeX: (value: number) => void
  onChangeY: (value: number) => void
  /** Combined callback — called with both values at once (avoids alternating action types) */
  onChange?: (x: number, y: number) => void
  /** When provided, dispatches only on mouseup instead of during drag */
  onCommitX?: (value: number) => void
  /** When provided, dispatches only on mouseup instead of during drag */
  onCommitY?: (value: number) => void
  /** Called on mouseup / input blur to seal the undo merge chain */
  onDragEnd?: () => void
}

const OffsetPad = ({
  valueX,
  valueY,
  min,
  max,
  minX,
  maxX,
  minY,
  maxY,
  step = 1,
  onChangeX,
  onChangeY,
  onChange,
  onCommitX,
  onCommitY,
  onDragEnd,
}: OffsetPadProps) => {
  const tokens = useAppTokens()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const draggingRef = useRef(false)
  // Local drag position — overrides prop values so the ball tracks the cursor in real-time
  const localXRef = useRef<number | null>(null)
  const localYRef = useRef<number | null>(null)

  // Resolved per-axis ranges
  const xMin = minX ?? min
  const xMax = maxX ?? max
  const yMin = minY ?? min
  const yMax = maxY ?? max
  const xRange = xMax - xMin
  const yRange = yMax - yMin

  // Convert value to canvas position (per axis)
  const valueToPxX = useCallback((val: number) => ((val - xMin) / xRange) * PAD_SIZE, [xMin, xRange])
  const valueToPxY = useCallback((val: number) => ((val - yMin) / yRange) * PAD_SIZE, [yMin, yRange])

  // Convert canvas position to value (per axis), snapped to step
  const pxToValueX = useCallback(
    (px: number) => {
      const clamped = Math.max(0, Math.min(PAD_SIZE, px))
      const raw = xMin + (clamped / PAD_SIZE) * xRange
      return Math.round(raw / step) * step
    },
    [xMin, xRange, step],
  )
  const pxToValueY = useCallback(
    (px: number) => {
      const clamped = Math.max(0, Math.min(PAD_SIZE, px))
      const raw = yMin + (clamped / PAD_SIZE) * yRange
      return Math.round(raw / step) * step
    },
    [yMin, yRange, step],
  )

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = PAD_SIZE * dpr
    canvas.height = PAD_SIZE * dpr
    ctx.scale(dpr, dpr)

    ctx.clearRect(0, 0, PAD_SIZE, PAD_SIZE)

    // Background
    ctx.fillStyle = tokens.sidebar.background
    ctx.beginPath()
    ctx.roundRect(0, 0, PAD_SIZE, PAD_SIZE, 6)
    ctx.fill()

    // Border
    ctx.strokeStyle = tokens.accordion.border
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.roundRect(0.5, 0.5, PAD_SIZE - 1, PAD_SIZE - 1, 6)
    ctx.stroke()

    // Crosshair lines
    const cx = PAD_SIZE / 2
    const cy = PAD_SIZE / 2
    ctx.strokeStyle = tokens.accordion.border
    ctx.lineWidth = 1
    ctx.setLineDash([3, 3])
    ctx.beginPath()
    ctx.moveTo(cx, 0)
    ctx.lineTo(cx, PAD_SIZE)
    ctx.moveTo(0, cy)
    ctx.lineTo(PAD_SIZE, cy)
    ctx.stroke()
    ctx.setLineDash([])

    // Handle position — use local drag values when available
    const hx = valueToPxX(localXRef.current ?? valueX)
    const hy = valueToPxY(localYRef.current ?? valueY)

    // Handle shadow
    ctx.beginPath()
    ctx.arc(hx, hy, HANDLE_RADIUS + 1, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
    ctx.fill()

    // Handle
    ctx.beginPath()
    ctx.arc(hx, hy, HANDLE_RADIUS, 0, Math.PI * 2)
    ctx.fillStyle = tokens.text.primary
    ctx.fill()
  }, [valueX, valueY, tokens, valueToPxX, valueToPxY])

  const updateFromEvent = useCallback(
    (e: PointerEvent | ReactPointerEvent) => {
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const vx = pxToValueX(x)
      const vy = pxToValueY(y)
      // Store local position for immediate canvas feedback
      localXRef.current = vx
      localYRef.current = vy
      if (onChange) {
        onChange(vx, vy)
      } else {
        onChangeX(vx)
        onChangeY(vy)
      }
      redrawCanvas()
    },
    [pxToValueX, pxToValueY, onChangeX, onChangeY, onChange, redrawCanvas],
  )

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      draggingRef.current = true
      updateFromEvent(e)
    },
    [updateFromEvent],
  )

  const handleInputX = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value)
      if (!Number.isNaN(val)) {
        const clamped = Math.max(xMin, Math.min(xMax, val))
        // Commit directly when commit callback exists (preview/commit pattern)
        if (onCommitX) onCommitX(clamped)
        else if (onChange) onChange(clamped, valueY)
        else onChangeX(clamped)
      }
    },
    [onChangeX, onCommitX, onChange, valueY, xMin, xMax],
  )

  const handleInputY = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value)
      if (!Number.isNaN(val)) {
        const clamped = Math.max(yMin, Math.min(yMax, val))
        // Commit directly when commit callback exists (preview/commit pattern)
        if (onCommitY) onCommitY(clamped)
        else if (onChange) onChange(valueX, clamped)
        else onChangeY(clamped)
      }
    },
    [onChangeY, onCommitY, onChange, valueX, yMin, yMax],
  )

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!draggingRef.current) return
      updateFromEvent(e)
    }
    const handlePointerUp = (e: PointerEvent) => {
      if (draggingRef.current) {
        draggingRef.current = false
        // Clear local drag state
        localXRef.current = null
        localYRef.current = null
        // Fire commit callbacks on release if provided
        if (onCommitX || onCommitY) {
          const canvas = canvasRef.current
          if (canvas) {
            const rect = canvas.getBoundingClientRect()
            const x = e.clientX - rect.left
            const y = e.clientY - rect.top
            if (onCommitX) onCommitX(pxToValueX(x))
            if (onCommitY) onCommitY(pxToValueY(y))
          }
        }
        onDragEnd?.()
      }
    }
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [updateFromEvent, onCommitX, onCommitY, pxToValueX, pxToValueY, onDragEnd])

  // Draw the pad (also redraws when props change without dragging)
  useEffect(() => {
    redrawCanvas()
  }, [redrawCanvas])

  const sx = buildStyles(tokens)

  return (
    <Box sx={sx.wrapper}>
      <canvas
        ref={canvasRef}
        style={{width: PAD_SIZE, height: PAD_SIZE, cursor: 'pointer', touchAction: 'none'}}
        onPointerDown={handlePointerDown}
      />
      <Box sx={sx.inputs}>
        <Box sx={sx.inputRow}>
          <Box component="span" sx={sx.inputLabel}>
            X
          </Box>
          <TextField
            value={valueX}
            onChange={handleInputX}
            onBlur={onDragEnd}
            size="small"
            type="number"
            slotProps={{htmlInput: {step}}}
            sx={sx.numberInput}
          />
        </Box>
        <Box sx={sx.inputRow}>
          <Box component="span" sx={sx.inputLabel}>
            Y
          </Box>
          <TextField
            value={valueY}
            onChange={handleInputY}
            onBlur={onDragEnd}
            size="small"
            type="number"
            slotProps={{htmlInput: {step}}}
            sx={sx.numberInput}
          />
        </Box>
      </Box>
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  inputs: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  inputLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: tokens.text.muted,
    minWidth: 12,
  },
  numberInput: {
    width: 64,
    '& .MuiOutlinedInput-root': {
      fontSize: 12,
      color: tokens.text.primary,
    },
    '& .MuiOutlinedInput-input': {
      padding: '4px 6px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
  },
})

export default OffsetPad
